import { useDispatch } from 'react-redux';

import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import ProductDescription from 'components/productdetail/description/ProductDescription';
import useMartyContext from 'hooks/useMartyContext';
import { toggleProductDescription } from 'actions/productDetail';

import styles from 'styles/components/productdetail/expandableProductDescription.scss';

const numberOfItemsToShowWhenCollapsed = 5;

interface ExpandableProductDescriptionProps {
  allowCollapse: boolean;
  defaultProductType: string;
  descriptionItems?: {
    bulletPoints: string[];
    sizeCharts: string[];
  };
  isExpanded: boolean;
  productId: string;
  setIsExpanded: (isExpanded: boolean) => void;
}

export const ExpandableProductDescription = ({
  allowCollapse,
  defaultProductType,
  descriptionItems,
  isExpanded,
  productId,
  setIsExpanded
}: ExpandableProductDescriptionProps) => {
  const { testId } = useMartyContext();
  const dispatch = useDispatch();
  let toggleButton;

  // If the bullet points are less than five, then pass isExpanded as true
  if (descriptionItems && descriptionItems.bulletPoints.length <= 5) {
    setIsExpanded(true);
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const { dataset } = event.target as HTMLElement;
    const { trackValue } = dataset;

    if (trackValue) {
      dispatch(toggleProductDescription(trackValue));
    }

    setIsExpanded(!isExpanded);
  };

  // Don't show expand collapse if there are less items then numberOfItemsToShowWhenCollapsed
  if (allowCollapse && descriptionItems && descriptionItems.bulletPoints.length > numberOfItemsToShowWhenCollapsed) {
    toggleButton = (
      <button type="button" className={styles.descriptionToggleButton} onClick={handleClick}>
        {isExpanded ? (
          <span
            data-track-action="Product-Page"
            data-track-label="Information"
            data-track-value="Show-less"
            data-test-id={testId('productDescriptionExpander')}
          >
            Show Less Information
          </span>
        ) : (
          <span
            data-track-action="Product-Page"
            data-track-label="Information"
            data-track-value="Show-More"
            data-test-id={testId('productDescriptionExpander')}
          >
            Show More Information
          </span>
        )}
      </button>
    );
  }

  return (
    <>
      <ProductDescription
        allowCollapse={allowCollapse}
        defaultProductType={defaultProductType}
        descriptionItems={descriptionItems}
        isExpanded={isExpanded}
        productId={productId}
      />
      {toggleButton}
    </>
  );
};

export default withErrorBoundary('ExpandableProductDescription', ExpandableProductDescription);
